import type { IconName } from '@mdvip/shared/components/blocks/Icon';
import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { TAB_TRIGGER_BASE, TAB_VARIANTS } from '@mdvip/shared/components/ui/tabs';
import { cn } from '@mdvip/shared/lib/utils';
import { Link } from 'react-router-dom';

/** A destination in the nav. A disabled item has no page yet, so it takes no `to`. */
export type NavTabItem<Id extends string = string> = {
  id: Id;
  label: string;
  icon?: IconName;
} & ({ to: string; disabled?: false } | { disabled: true; to?: never });

interface NavTabsProps<Id extends string> {
  items: readonly NavTabItem<Id>[];
  /** The id of the item for the current page. */
  active: Id;
  /** Accessible name for the `<nav>` landmark. */
  label: string;
  /** Caption shown on every disabled item, e.g. "Coming soon". */
  disabledHint?: string;
  className?: string;
}

const TRIGGER = cn(TAB_TRIGGER_BASE, TAB_VARIANTS.pills.trigger);

/**
 * Navigation between sibling pages, drawn as the design system's `pills` tabs.
 *
 * Each item is its own route, so these are links in a `<nav>` with `aria-current` — not the `Tabs`
 * primitive. A tablist would announce separate documents as panels of one page and break
 * middle-click and open-in-new-tab. For switching views within one page, use `PageTabs`.
 */
export function NavTabs<Id extends string>({ items, active, label, disabledHint, className }: NavTabsProps<Id>) {
  return (
    <nav aria-label={label} className={cn('@container', className)}>
      {/* A row that outgrows its container scrolls sideways rather than wrapping. The bleed
          (`-mx-4 px-4`) runs the scroll to the page gutter instead of clipping at the column, and
          `-my-1 py-1` keeps the focus ring clear of the vertical clip the overflow also applies. */}
      <div className="-mx-4 -my-1 overflow-x-auto px-4 py-1 [scrollbar-width:none]">
        <div data-slot="tabs-list" data-variant="pills" className={cn(TAB_VARIANTS.pills.list, 'w-max min-w-full')}>
          {items.map((item) => {
            if (item.disabled) {
              return (
                <span key={item.id} data-slot="tabs-trigger" aria-disabled="true" className={TRIGGER}>
                  {item.icon && <Icon name={item.icon} size="xs" className="text-muted-foreground" />}
                  {item.label}
                  {disabledHint && <span className="text-xs font-normal text-muted-foreground">{disabledHint}</span>}
                </span>
              );
            }
            const isActive = item.id === active;
            return (
              <Link
                key={item.id}
                to={item.to}
                data-slot="tabs-trigger"
                // Every `data-active:` rule in the pill treatment keys off this attribute, which Base
                // UI's Tabs emits for us. Nothing emits it here, so the active link sets it by hand.
                data-active={isActive ? '' : undefined}
                aria-current={isActive ? 'page' : undefined}
                className={TRIGGER}
              >
                {item.icon && (
                  <Icon
                    name={item.icon}
                    size="xs"
                    weight={isActive ? 'solid' : 'regular'}
                    className={isActive ? undefined : 'text-muted-foreground'}
                  />
                )}
                {item.label}
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
